import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CustomerSubscriptionChannel,
  CustomerSubscriptionSource,
  CustomerSubscriptionStatus,
} from '@lambda/apis/src/customer/enum';
import { CtaButton } from '@/components/Cta/Cta';
import CognitoPassowrdReq from '@/components/commons/Customer/CognitoPasswordReq';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import {
  trackSignup,
  trackSubscriptionPreferencesUpdated,
} from '@/lib/analytics/trackCustomer';
import openEyeIcon from '@/public/icons/account/eye_open.svg';
import closedEyeIcon from '@/public/icons/account/eye_closed.svg';
import i18n from '@/settings/i18n';
import Notice from './Notice';
import {
  BusinessSignupForm,
  INPUT_CLASS,
  businessSignupSchema,
} from './constants';

const t = i18n.init('business');
const tAuth = i18n.init('auth');

const FieldError = ({ message }: { message?: string }) =>
  message ? <div className="mt-1 text-xs text-red">{message}</div> : null;

/**
 * The business account sign-up: the regular account fields plus the legal
 * business name, which is saved once the account exists.
 */
const SignupForm = ({
  onStart,
  onDone,
}: {
  /** The account is being created: keep this form up until `onDone`. */
  onStart: () => void;
  /** Done; with the business name when the account exists but it isn't saved. */
  onDone: (unsavedBusinessName?: string) => void;
}) => {
  const form = useForm<BusinessSignupForm>({
    resolver: yupResolver(businessSignupSchema),
    defaultValues: { acceptsMarketing: true },
  });
  const { errors } = form.formState;
  const {
    cognitoSignup,
    cognitoLogin,
    updateEmailSubscription,
    updateBusinessName,
  } = useCustomerContext();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const consentMessage = tAuth`Subscribe to our newsletter for exclusives & early access.`;

  const submit = async (input: BusinessSignupForm) => {
    setSubmitting(true);
    setError('');
    onStart();

    // Set once the account exists: the name to retry if it wasn't saved.
    let unsavedBusinessName: string | undefined;

    try {
      const {
        customer,
        error: signupError,
        message,
      } = await cognitoSignup({
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        password: input.password,
      });

      if (signupError || !customer) {
        setError(message ?? tAuth`Unable to sign up. Please try again later.`);

        return;
      }

      // Failures resolve too, as `{ message, error }`: a sign-in only
      // succeeded when it returns the customer.
      const login = await cognitoLogin(input.email, input.password);

      if (login.error || !login.customer) {
        setError(
          t`Your account was created, but we couldn't sign you in. Please log in to continue.`,
        );

        return;
      }

      unsavedBusinessName = input.businessName;
      trackSignup();

      const status = input.acceptsMarketing
        ? CustomerSubscriptionStatus.SUBSCRIBED
        : CustomerSubscriptionStatus.UNSUBSCRIBED;

      await updateEmailSubscription({
        customerId: customer.reebeloId,
        status,
        source: CustomerSubscriptionSource.SIGNUP,
        consentMessage,
      });
      trackSubscriptionPreferencesUpdated(
        input.email,
        status,
        CustomerSubscriptionChannel.EMAIL,
        CustomerSubscriptionSource.SIGNUP,
      );

      if (await updateBusinessName(input.businessName)) {
        unsavedBusinessName = undefined;
      }
    } catch (err) {
      setError(tAuth`Unable to sign up. Please try again later.`);
    } finally {
      setSubmitting(false);
      // Signed in or not, the page now follows the customer's state.
      onDone(unsavedBusinessName);
    }
  };

  return (
    <form
      className="flex flex-col gap-2 text-sm"
      onSubmit={form.handleSubmit(submit)}
      noValidate
    >
      <Notice>
        {t`Important: your legal business name must be the same as on your exemption certificate.`}
      </Notice>
      <div>
        <input
          className={INPUT_CLASS}
          placeholder={t`Legal business name`}
          aria-label={t`Legal business name`}
          aria-invalid={!!errors.businessName}
          autoComplete="organization"
          {...form.register('businessName')}
        />
        <FieldError message={errors.businessName?.message} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <input
            className={INPUT_CLASS}
            placeholder={tAuth`First name`}
            aria-label={tAuth`First name`}
            aria-invalid={!!errors.firstName}
            autoComplete="given-name"
            {...form.register('firstName')}
          />
          <FieldError message={errors.firstName?.message} />
        </div>
        <div>
          <input
            className={INPUT_CLASS}
            placeholder={tAuth`Last name`}
            aria-label={tAuth`Last name`}
            aria-invalid={!!errors.lastName}
            autoComplete="family-name"
            {...form.register('lastName')}
          />
          <FieldError message={errors.lastName?.message} />
        </div>
      </div>
      <div>
        <input
          className={INPUT_CLASS}
          placeholder={tAuth`Email`}
          aria-label={tAuth`Email`}
          aria-invalid={!!errors.email}
          type="email"
          autoComplete="email"
          {...form.register('email')}
        />
        <FieldError message={errors.email?.message} />
      </div>
      <div>
        <div className="relative">
          <input
            className={INPUT_CLASS}
            placeholder={tAuth`Password`}
            aria-label={tAuth`Password`}
            aria-invalid={!!errors.password}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...form.register('password')}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <Image
              src={showPassword ? openEyeIcon : closedEyeIcon}
              alt={tAuth`Toggle Password`}
              height={50}
              width={50}
              className="h-full w-full"
            />
          </button>
        </div>
        <FieldError message={errors.password?.message} />
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-700/60">
        <input
          type="checkbox"
          className="accent-gray-700"
          {...form.register('acceptsMarketing')}
        />
        {consentMessage}
      </label>
      <p className="text-xs text-gray-700/60">
        {tAuth`By signing up, you agree to our`}{' '}
        <Link
          href="/policies/terms-of-service"
          className="text-[#0D6EFD] underline underline-offset-1"
        >
          {tAuth`T&Cs`}
        </Link>{' '}
        {tAuth`and`}{' '}
        <Link
          href="/policies/privacy-policy"
          className="text-[#0D6EFD] underline underline-offset-1"
        >
          {tAuth`Privacy Policy`}
        </Link>
      </p>
      <CtaButton
        type="submit"
        className="mt-1 !h-11 w-full !rounded !text-sm"
        loading={submitting}
      >
        {tAuth`Create my account`}
      </CtaButton>
      {error && <div className="text-xs text-red">{error}</div>}
      <CognitoPassowrdReq password={form.watch('password')} />
    </form>
  );
};

export default SignupForm;
